export {Subscribe as Page};

import {useContext, useState, useEffect} from "preact/hooks";
import messages from "../../messages/messages.js";
import {Helmet} from "react-helmet-async";
import {PaymentMethods} from "../../components/PaymentMethods.jsx";
import {useAuth} from '../../components/AuthProvider.jsx';
import API_URL from "../../includes/api.js";
import {NotificationContext} from "../../components/NotificationContext.jsx";
import {sendEmailVerification} from "firebase/auth";

function Subscribe() {

    const {user, getToken, authLoading} = useAuth();
    const {addNotification} = useContext(NotificationContext);
    const [dialog, setDialog] = useState({open: false, type: null});
    const [loadingTier, setLoadingTier] = useState(null);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [currency, setCurrency] = useState('EUR');

    useEffect(() => {
        try {
            const lang = (navigator.language || navigator.userLanguage || '').toUpperCase();
            if (lang.includes('US')) {
                setCurrency('USD');
            } else if (lang.includes('GB')) {
                setCurrency('GBP');
            } else {
                setCurrency('EUR');
            }
        } catch (e) {
        }
    }, []);

    const handleResendVerification = async () => {
        if (!user || resendCooldown > 0) return;
        try {
            await sendEmailVerification(user, {url: "https://" + messages["DOMAIN"]});
            addNotification('Verification email sent. Please check your inbox.', 'success');
            setResendCooldown(60);
            const interval = setInterval(() => {
                setResendCooldown(prev => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            setDialog({open: false, type: null});
        } catch (e) {
            addNotification('Failed to send verification email.', 'error');
        }
    };

    const handleCheckout = async (tier) => {
        if (authLoading) return;
        if (!user) {
            setDialog({open: true, type: 'auth'});
            return;
        }

        if (!user.emailVerified) {
            setDialog({open: true, type: 'verify'});
            return;
        }

        // Force a token refresh so backend sees the updated emailVerified flag
        const token = await getToken(true);
        if (!token) {
            setDialog({open: true, type: 'auth'});
            return;
        }
        setLoadingTier(tier.key);

        const formData = new FormData();
        formData.append("firebase_auth_token", token);
        formData.append("tier", tier.key);
        formData.append("source", `https://www.` + messages['DOMAIN']);
        formData.append("currency", currency);

        fetch(API_URL + "/billing/checkout_link", {
            method: "POST",
            body: formData,
        })
            .then(response => response.json().then(data => ({ok: response.ok, data})))
            .then(({ok, data}) => {
                const checkoutUrl = data?.data?.payment_link || data?.data?.checkout_url || data?.data?.url;
                if (ok && data?.code === 200 && checkoutUrl) {
                    const checkoutWindow = window.open(checkoutUrl, "_blank", "noopener,noreferrer");
                    if (!checkoutWindow) {
                        addNotification("Please allow popups to continue to checkout.", "warning");
                    }
                    return;
                }
                addNotification(data?.message, "error");
            })
            .catch((error) => {
                addNotification(error?.message, "error");
            })
            .finally(() => {
                setLoadingTier(null);
            });
    };

    const tiers = [
        {
            key: "premium",
            title: "Premium",
            price_eur: 3.99,
            price_usd: 4.99,
            price_gbp: 3.99,
            period: "month",
            perks: [
                "🌐 Personal " + messages["SITE.NAME"] + " Website",
                ...(messages["SITE.NAME"] !== "Lofi Tune" ? ["🎶 Full Access to " + messages["SITE.NAME"] + " Maker"] : []),
                "📢 Promotion schedule",
                "💡 Song idea generator",
                (messages["show_custom_designer"] === "true") ? "🎨 Custom channel artwork" : "",
            ].filter(Boolean),
            roles: ["💎 Subscriber"],
        },
        {
            key: "premium_plus",
            title: "Premium+",
            price_eur: 8.99,
            price_usd: 9.99,
            price_gbp: 8.99,
            period: "month",
            perks: [
                "🎖️  A custom role in Discord just for you",
                "🚀  Priority support + faster responses",
                "❤️  Eternal love from our team",
                "➕  Everything from Premium role",
            ],
            roles: ["💎 Subscriber", "🎉 Donator"],
        },
        {
            key: "donate",
            title: "Donate",
            price_eur: null,
            price_usd: null,
            price_gbp: null,
            period: "amount",
            perks: [
                "🙏  Support development directly",
                "❤️  Eternal love from our team",
            ],
            roles: ["🎉 Donator"],
            disabled: true
        },
    ];

    return (
        <>
            {dialog.open && (
                <div className="fixed inset-0 z-50 bg-black/60 p-4 flex justify-center items-start"
                     onClick={() => setDialog({open: false, type: null})}>
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="subscribe-dialog-title"
                        className="relative bg-white rounded-lg shadow-lg w-full max-w-md mx-auto mt-30 p-6 text-center text-black"
                        onClick={e => e.stopPropagation()}>
                        {dialog.type === 'verify' && (
                            <>
                                <h2 id="subscribe-dialog-title" className="text-xl mb-3 text-gray-900">Email verification required</h2>
                                <p className="text-sm text-gray-700 mb-6">
                                    Your email is not verified. Please check your inbox and verify your email to
                                    continue.
                                </p>
                                <button
                                    className="cursor-pointer primary-btn text-white mb-2"
                                    onClick={handleResendVerification}
                                    disabled={resendCooldown > 0}
                                >
                                    {resendCooldown > 0 ? `Resend (${resendCooldown})` : 'Resend verification email'}
                                </button>
                            </>
                        )}
                        {dialog.type === 'auth' && (
                            <>
                                <h2 id="subscribe-dialog-title" className="text-xl mb-3 text-gray-900">Login required</h2>
                                <p className="text-sm text-gray-700 mb-6">
                                    Please login or create an account before starting checkout.
                                </p>
                                <div className="flex justify-center gap-3">
                                    <a href="/account" className="cursor-pointer primary-btn text-white">Log in</a>
                                    <a href="/signup" className="cursor-pointer secondary-btn text-red-400">Sign up</a>
                                </div>
                            </>
                        )}
                        <div>
                            <button className="cursor-pointer secondary-btn text-red-400"
                                    onClick={() => setDialog({open: false, type: null})}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <section className="text-white min-h-screen py-10 px-4">

                <Helmet>
                    <title>{messages["META.SUBSCRIBE.TITLE"]}</title>
                    <meta name="description" content={messages["META.SUBSCRIBE.DESC"]}/>
                </Helmet>

                <div class="text-center max-w-4xl mt-6 mb-14">
                    <h1>Become a {messages['SITE.NAME']} Subscriber</h1>
                    <p>Support {messages['DOMAIN']} and enjoy subscriber-only features and exclusive updates.
                        Be part of a passionate community that shares your love for {messages['SITE.NAME']} music.</p>
                </div>

                <div className="flex justify-center gap-2 mb-8" role="group" aria-label="Select currency">
                    <button
                        type="button"
                        aria-pressed={currency === 'EUR'}
                        onClick={() => setCurrency('EUR')}
                        className={`cursor-pointer px-3 py-1 rounded-full ${currency === 'EUR' ? 'bg-red-400 text-white' : 'bg-gray-700 text-white'}`}>
                        EUR
                    </button>
                    <button
                        type="button"
                        aria-pressed={currency === 'USD'}
                        onClick={() => setCurrency('USD')}
                        className={`cursor-pointer px-3 py-1 rounded-full ${currency === 'USD' ? 'bg-red-400 text-white' : 'bg-gray-700 text-white'}`}>
                        USD
                    </button>
                    <button
                        type="button"
                        aria-pressed={currency === 'GBP'}
                        onClick={() => setCurrency('GBP')}
                        className={`cursor-pointer px-3 py-1 rounded-full ${currency === 'GBP' ? 'bg-red-400 text-white' : 'bg-gray-700 text-white'}`}>
                        GBP
                    </button>
                </div>

                <div className="grid gap-10 lg:grid-cols-3 w-full max-w-6xl mx-auto">
                    {tiers.map((tier) => {
                                                        const priceLabel = (currency === 'EUR')
                                                            ? (tier.price_eur ? `€${tier.price_eur.toFixed(2).replace('.', ',')}` : 'Custom')
                                                            : (currency === 'USD')
                                                                ? (tier.price_usd ? `$${tier.price_usd.toFixed(2)}` : 'Custom')
                                                                : (tier.price_gbp ? `£${tier.price_gbp.toFixed(2)}` : 'Custom');
                                                        const displayPeriod = (currency === 'EUR') ? 'EUR / month' : (currency === 'USD' ? 'USD / month' : 'GBP / month');

                        return (
                            <div key={tier.title} className="w-full">
                                <div
                                    className={`bg-zinc-800 relative rounded-lg shadow-lg flex flex-col h-full ${
                                        tier.title === "Premium+" ? "outline-solid outline-4 outline-red-400" : ""
                                    }`}
                                >
                                    {tier.title === "Premium+" && (
                                        <div class="absolute left-0 right-0 max-w-1/2 mx-auto -top-5 bg-red-400 text-center text-white px-4 py-1 rounded-lg">
                                            Most Popular
                                        </div>
                                    )}

                                    <div className="h-44 w-full bg-gradient-to-br from-zinc-700 to-zinc-800 rounded-t-lg flex items-center justify-center">
                                        <img
                                            src={`/img/` + tier.title + `.webp`}
                                            alt={tier.title}
                                            className="h-30 w-30 rounded-lg object-cover"
                                        />
                                    </div>

                                    {/* Card Content */}
                                    <div className="p-6 flex flex-col flex-1">
                                        <h2 className="uppercase text-xl text-white mb-2 text-center">
                                            {tier.title}
                                        </h2>
                                        <p className="text-center text-zinc-400 mb-4">
                                            <span className="text-3xl font-bold text-white">{priceLabel}</span>
                                            <br/>{displayPeriod || tier.period}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => handleCheckout(tier)}
                                            disabled={tier.disabled || authLoading || loadingTier === tier.key}
                                            className="cursor-pointer mb-6 bg-linear-to-br text-center from-red-400 to-[#e12d39] leading-none text-white py-3 px-8 rounded-full shadow-md transition-all hover:translate-y-[-2px] duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            {tier.disabled
                                                ? "Coming soon"
                                                : (loadingTier === tier.key
                                                    ? "Loading..."
                                                    : (tier.title === "Donate" ? "Donate" : "Subscribe")
                                                )
                                            }
                                        </button>

                                        <p className="uppercase text-sm text-zinc-400 mb-2 tracking-wider">
                                            Exclusive Roles
                                        </p>
                                        <div className="mb-4 space-y-1 text-sm list-none flex gap-2">
                                            {tier.roles.map((r) => (
                                                <div key={r} class="bg-gray-700 mb-0 text-white text-xs font-medium px-3 py-1 rounded-sm">{r}</div>
                                            ))}
                                        </div>

                                        <p className="uppercase text-sm text-zinc-400 mb-2 tracking-wider">
                                            Perks
                                        </p>
                                        <ul className="space-y-1 text-sm flex-1 list-none">
                                            {tier.perks.map((p) => (
                                                <li key={p}>{p}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <PaymentMethods class="mt-10" />
            </section>
        </>
    );
}
